import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import CustomRadio from "../../../../components/common/CustomRadio/CustomRadio";
import CustomMonthYear from "../../../../components/common/CustomMonthYear/CustomMonthYear";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import { Form, FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
   isValidFileType,
} from "../../../../helpers/fileValidationRules";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import RouteNames from "../../../../AppRoutes/RouteName";
import { addVehicleAndEmiDetailsAsync } from "../../../../redux/features/fleetMaster";
import { useNavigate } from "react-router-dom";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";

const validationSchema = Yup.object().shape({
   vehicleNo: Yup.string().required("Vehicle No is required"),
   chasisNo: Yup.string().required("Chassis No is required"),
   engineNo: Yup.string().required("Engine No is required"),
   vehicleOwnerName: Yup.string().required("Vehicle Owner Name is required"),
   dimension: Yup.string().required("Dimension is required"),
   fastagBankName: Yup.string().required("Fastag Bank Name is required"),
   vehicleCategory: Yup.string().required("Vehicle Category is required"),
   tonnage: Yup.string().required("Tonnage is required"),
   fuelType: Yup.string().required("Fuel Type is required"),
   make: Yup.string().required("Make Name is required"),
   model: Yup.date()
      .required("Model Month Year is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   registrationCertificate: Yup.mixed()
      .required("Registration Certificate is required")
      .test("is-valid-type", "Not a valid document type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "all")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   insuranceExpiryDate: Yup.date()
      .required("Insurance Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   insuranceAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("Insurance Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   insuranceCertificate: Yup.mixed()
      .required("Insurance Certificate is required")
      .test("is-valid-type", "Not a valid document type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "all")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   fitnessExpiryDate: Yup.date()
      .required("Fitness Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   fitnessAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("Fitness Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   fitnessCertificate: Yup.mixed()
      .required("Fitness Certificate is required")
      .test("is-valid-type", "Not a valid document type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "all")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   localPermitExpiryDate: Yup.date()
      .required("Local Permit Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   localPermitAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("Local Permit Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   localPermitDocument: Yup.mixed()
      .required("Local Permit Document is required")
      .test("is-valid-type", "Not a valid document type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "all")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   nationalPermitExpiryDate: Yup.date()
      // .required("National Permit Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   nationalPermitAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      // .required("National Permit Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   nationalPermitDocument: Yup.mixed()
      .nullable()
      // .required("National Permit Document is required")

      .test("is-valid-type", "Not a valid document type", (value) =>
         value ? isValidFileType(value && value.name.toLowerCase(), "all") : true
      )
      .test("is-valid-size", MAX_FILE_SIZE_MESSAGE, (value) =>
         value ? value.size <= MAX_FILE_SIZE : true
      ),
   pucExpiryDate: Yup.date()
      .required("PUC Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   pucAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("PUC Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   pucDocument: Yup.mixed()
      .required("File is required")
      .test("is-valid-type", "Not a valid document type", (value) => {
         return isValidFileType(value && value.name.toLowerCase(), "all");
      })
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   mvTax: Yup.mixed()
      .required("MV Tax is required")
      .test("is-valid-type", "Not a valid document type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "all")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   mvTaxExpiryDate: Yup.date()
      .required("MV Tax Expiry Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   mvTaxAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("MV Tax Amount is required and must be a number")
      .min(0, "Must be a positive value"),
   gpsProviderName: Yup.string(),
   gpsAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0, "Must be a positive value"),
   fabricatorName: Yup.string(),
   fabricatorLocation: Yup.string(),

   // ----------------------- EMI -----------------------
   emiStartDate: Yup.date()
      .required("EMI Start Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   emiEndDate: Yup.date()
      .required("EMI End Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   emiAmount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required("EMI Amount is required and must be a number")
      .min(0, "Must be a positive value"),

   emiCertificate: Yup.mixed()
      .required("EMI Certificate is required")
      .test("is-valid-type", "Not a valid document type", (value) => {
         return isValidFileType(value && value.name.toLowerCase(), "all");
      })
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   financedBy: Yup.string().required("Financed By Name is required"),
});

const initialValues = {
   vehicleNo: "",
   chasisNo: "",
   engineNo: "",
   vehicleOwnerName: "",
   dimension: "",
   fastagBankName: "",
   vehicleCategory: "",
   tonnage: "",
   fuelType: "Diesel",
   make: "",
   model: "",
   registrationCertificate: null,
   insuranceExpiryDate: "",
   insuranceAmount: 0,
   insuranceCertificate: null,
   fitnessExpiryDate: "",
   fitnessAmount: 0,
   fitnessCertificate: null,
   localPermitExpiryDate: "",
   localPermitAmount: 0,
   localPermitDocument: null,
   nationalPermitExpiryDate: "",
   nationalPermitAmount: 0,
   nationalPermitDocument: null,
   pucExpiryDate: "",
   pucAmount: 0,
   pucDocument: null,
   mvTax: null,
   mvTaxExpiryDate: "",
   mvTaxAmount: 0,
   gpsProviderName: "",
   gpsAmount: 0,
   fabricatorName: "",
   fabricatorLocation: "",

   // ------------------------- EMI -----------------------------
   emiStartDate: "",
   emiEndDate: "",
   emiAmount: "",
   emiCertificate: null,
   financedBy: "",
};

function VehicleDetails({ step1Data, setStep1Data, setCurrentTabIndex }) {
   const navigate = useNavigate();
   const dispatch = useDispatch();

   const [initialState, setInitialState] = useState(initialValues);

   const fleetMaster = useSelector((state) => state.fleetMaster);

   const tonnageData = fleetMaster?.tonnage?.map?.((item) => ({
      label: item?.tonnage,
      value: item?.id,
   }));
   const makeData = fleetMaster?.make?.map?.((item) => ({
      label: item?.make,
      value: item?.id,
   }));
   const dimensionData = fleetMaster?.dimension?.map?.((item) => ({
      label: item?.dimension,
      value: item?.id,
   }));
   const vehicleCategoryData = fleetMaster?.vehicleCategory?.map?.((item) => ({
      label: item?.category_name,
      value: item?.id,
   }));

   const handleAddVehicleDetails = (formData) => {
      const vehicleDetails = { ...formData };

      const data = {
         vehicle_no: vehicleDetails?.vehicleNo,
         chasis_no: vehicleDetails?.chasisNo,
         engine_no: vehicleDetails?.engineNo,
         vehicle_owner_name: vehicleDetails?.vehicleOwnerName,
         dimension_id: vehicleDetails?.dimension,
         fastag_bank_name: vehicleDetails?.fastagBankName,
         vehicle_category_id: vehicleDetails?.vehicleCategory,
         tonnage_id: vehicleDetails?.tonnage,
         fuel_type: vehicleDetails?.fuelType,
         make_id: vehicleDetails?.make,
         model: vehicleDetails?.model,
         registration_certificate: vehicleDetails?.registrationCertificate,
         insurance_expiry_date: vehicleDetails?.insuranceExpiryDate,
         insurance_amount: vehicleDetails?.insuranceAmount,
         insurance_certificate: vehicleDetails?.insuranceCertificate,
         fitness_expiry_date: vehicleDetails?.fitnessExpiryDate,
         fitness_amount: vehicleDetails?.fitnessAmount,
         fitness_certificate: vehicleDetails?.fitnessCertificate,
         local_permit_expiry_date: vehicleDetails?.localPermitExpiryDate,
         local_permit_amount: vehicleDetails?.localPermitAmount,
         local_permit_document: vehicleDetails?.localPermitDocument,
         national_permit_expiry_date: vehicleDetails?.nationalPermitExpiryDate,
         national_permit_amount: vehicleDetails?.nationalPermitAmount,
         national_permit_document: vehicleDetails?.nationalPermitDocument,
         puc_expiry_date: vehicleDetails?.pucExpiryDate,
         puc_amount: vehicleDetails?.pucAmount,
         puc_document: vehicleDetails?.pucDocument,
         mv_tax_expiry_date: vehicleDetails?.mvTaxExpiryDate,
         mv_tax_amount: vehicleDetails?.mvTaxAmount,
         mv_tax_document: vehicleDetails?.mvTax,
         gps_provider_name: vehicleDetails?.gpsProviderName,
         gps_amount: vehicleDetails?.gpsAmount,
         fabricator_name: vehicleDetails?.fabricatorName,
         fabricator_location: vehicleDetails?.fabricatorLocation,

         emi_start_date: vehicleDetails?.emiStartDate,
         emi_end_date: vehicleDetails?.emiEndDate,
         emi_amount: vehicleDetails?.emiAmount,
         emi_certificate: vehicleDetails?.emiCertificate,
         financed_by: vehicleDetails?.financedBy,

         model_id: 3,
         action_id: 1,
      };

      const newData = new FormData();

      Object.keys(data).forEach((key) => {
         newData.append(key, data[key]);
      });

      dispatch(backdropLoadingAction(true));
      dispatch(
         addVehicleAndEmiDetailsAsync({
            data: newData,
            err: () => {
               dispatch(backdropLoadingAction(false));
               toast.error("Something went wrong !");
            },
            done: () => {
               dispatch(backdropLoadingAction(false));
               toast.success("Record added successfully !");
               navigate(RouteNames.fleetMasterList);
            },
         })
      );
   };

   useEffect(() => {
      if (Object.keys(step1Data).length > 0) {
         setInitialState((prev) => ({ ...prev, ...step1Data }));
      }
   }, []);

   const formik = useFormik({
      initialValues: initialState,
      validationSchema: validationSchema,
      enableReinitialize: true,
      onSubmit: handleAddVehicleDetails,
   });

   const { errors, values, touched, getFieldProps, handleSubmit, setFieldValue } = formik;

   return (
      <FormikProvider value={formik}>
         <Form noValidate>
            <div
               className="tab-pane fade show active"
               id="0"
               role="tabpanel"
               aria-labelledby="0"
            >
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Vehicle Details</h3>
                  </div>

                  <div className="row card-body">
                     {/* Vehicle No Sec */}
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Vehicle No"
                           id="vehicleNo"
                           placeholder="Enter Vehicle No"
                           {...getFieldProps("vehicleNo")}
                           errors={
                              Boolean(errors.vehicleNo) && Boolean(touched.vehicleNo)
                           }
                           message={errors.vehicleNo}
                        />
                     </div>
                     <div className="col-lg-4">
                        {/* Vehicle No Sec */}
                        <CustomInput
                           require
                           label="Chassis No"
                           id="chasisNo"
                           placeholder="Enter Chassis No"
                           {...getFieldProps("chasisNo")}
                           errors={Boolean(errors.chasisNo) && Boolean(touched.chasisNo)}
                           message={errors.chasisNo}
                        />
                     </div>
                     <div className="col-lg-4">
                        {/* Vehicle No Sec */}
                        <CustomInput
                           require
                           label="Engine No"
                           id="engineNo"
                           placeholder="Enter Engine No"
                           onChange={formik.handleChange}
                           {...getFieldProps("engineNo")}
                           errors={Boolean(errors.engineNo) && Boolean(touched.engineNo)}
                           message={errors.engineNo}
                        />
                     </div>

                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Vehicle owner name"
                           id="vehicleOwner"
                           placeholder="Enter vehicle owner name"
                           {...getFieldProps("vehicleOwnerName")}
                           errors={
                              Boolean(errors.vehicleOwnerName) &&
                              Boolean(touched.vehicleOwnerName)
                           }
                           message={errors.vehicleOwnerName}
                        />
                     </div>

                     {/* Dimension  */}
                     <div className="col-lg-4">
                        <CustomDropdown
                           label="Dimension(L B H (ft))"
                           optionData={dimensionData}
                           {...getFieldProps("dimension")}
                           onChange={(value) => {
                              setFieldValue("dimension", value?.[0]?.value, true);
                           }}
                           errors={
                              Boolean(errors.dimension) && Boolean(touched.dimension)
                           }
                           message={errors.dimension}
                        />
                     </div>

                     {/* Fastag bank name Sec */}
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Fastag bank name"
                           id="fastagBankName"
                           placeholder="Enter Fastag bank name"
                           {...getFieldProps("fastagBankName")}
                           errors={
                              Boolean(errors.fastagBankName) &&
                              Boolean(touched.fastagBankName)
                           }
                           message={errors.fastagBankName}
                        />
                     </div>
                     {/* Vehicle Category Sec */}
                     <div className="col-lg-4">
                        <CustomDropdown
                           label="Vehicle Category"
                           optionData={vehicleCategoryData}
                           {...getFieldProps("vehicleCategory")}
                           onChange={(value) => {
                              setFieldValue("vehicleCategory", value?.[0]?.value);
                           }}
                           errors={
                              Boolean(errors.vehicleCategory) &&
                              Boolean(touched.vehicleCategory)
                           }
                           message={errors.vehicleCategory}
                        />
                     </div>
                     {/* Tonnage Sec */}
                     <div className="col-lg-4">
                        <CustomDropdown
                           label="Tonnage(T)"
                           optionData={tonnageData}
                           {...getFieldProps("tonnage")}
                           onChange={(value) => {
                              setFieldValue("tonnage", value?.[0]?.value);
                           }}
                           errors={Boolean(errors.tonnage) && Boolean(touched.tonnage)}
                           message={errors.tonnage}
                        />
                     </div>

                     <div className="col-lg-4">
                        <label className="text-bold">
                           Fuel Type
                           <span className="text-danger">*</span>
                        </label>
                        <div className="form-group mt-2">
                           <CustomRadio
                              label="Diesel"
                              id="diesel"
                              value="Diesel"
                              name="fuelType"
                              defaultChecked={values.fuelType}
                              onChange={getFieldProps("fuelType").onChange}
                              // onChange={(event) => {
                              //    setFieldValue("fuelType", event.target.value);
                              //    setFuelType(event.target.value);
                              // }}
                           />
                           <CustomRadio
                              label="CNG"
                              id="cng"
                              value="CNG"
                              name="fuelType"
                              onChange={getFieldProps("fuelType").onChange}
                              // onChange={(event) => {
                              //    setFieldValue("fuelType", event.target.value);
                              //    setFuelType(event.target.value);
                              // }}
                           />
                        </div>
                     </div>

                     <div className="col-lg-4">
                        <CustomDropdown
                           label="Make"
                           optionData={makeData}
                           {...getFieldProps("make")}
                           onChange={(value) => {
                              setFieldValue("make", value?.[0]?.value);
                           }}
                           errors={Boolean(errors.make) && Boolean(touched.make)}
                           message={errors.make}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomMonthYear
                           label="Model (Month Year)"
                           {...getFieldProps("model")}
                           // onChange={(value) => {
                           //    setFieldValue("model", value?.[0]?.value);
                           // }}
                           errors={Boolean(errors.model) && Boolean(touched.model)}
                           message={errors.model}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Registration Certificate
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue(
                                 "registrationCertificate",
                                 event?.target?.files?.[0]
                              );
                           }}
                           value={getFieldProps("registrationCertificate").value}
                           errors={
                              Boolean(errors.registrationCertificate) &&
                              Boolean(touched.registrationCertificate)
                           }
                           message={errors.registrationCertificate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="Insurance Expiry Date"
                           placeholder="Enter Insurance Expiry Date"
                           {...getFieldProps("insuranceExpiryDate")}
                           errors={
                              Boolean(errors.insuranceExpiryDate) &&
                              Boolean(touched.insuranceExpiryDate)
                           }
                           message={errors.insuranceExpiryDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Insurance Amount"
                           id="fitnessAmount"
                           placeholder="Enter Insurance Amount"
                           {...getFieldProps("insuranceAmount")}
                           errors={
                              Boolean(errors.insuranceAmount) &&
                              Boolean(touched.insuranceAmount)
                           }
                           message={errors.insuranceAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Insurance Certificate
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue(
                                 "insuranceCertificate",
                                 event?.target?.files?.[0]
                              );
                           }}
                           value={getFieldProps("insuranceCertificate").value}
                           errors={
                              Boolean(errors.insuranceCertificate) &&
                              Boolean(touched.insuranceCertificate)
                           }
                           message={errors.insuranceCertificate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="Fitness Expiry Date"
                           placeholder="Enter Fitness Expiry Date"
                           {...getFieldProps("fitnessExpiryDate")}
                           errors={formik.errors.fitnessExpiryDate}
                           message={formik.errors.fitnessExpiryDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Fitness Amount"
                           id="fitnessAmount"
                           placeholder="Enter Fitness Amount"
                           {...getFieldProps("fitnessAmount")}
                           errors={formik.errors.fitnessAmount}
                           message={formik.errors.fitnessAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Fitness Certificate
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue(
                                 "fitnessCertificate",
                                 event?.target?.files?.[0]
                              );
                           }}
                           value={getFieldProps("fitnessCertificate").value}
                           errors={formik.errors.fitnessCertificate}
                           message={formik.errors.fitnessCertificate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="Local Permit Expiry Date"
                           placeholder="Enter Local Permit Expiry Date"
                           {...getFieldProps("localPermitExpiryDate")}
                           errors={formik.errors.localPermitExpiryDate}
                           message={formik.errors.localPermitExpiryDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Local Permit Amount"
                           id="fitnessAmount"
                           placeholder="Enter Local Permit Amount"
                           {...getFieldProps("localPermitAmount")}
                           errors={formik.errors.localPermitAmount}
                           message={formik.errors.localPermitAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           Local Permit Document
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue(
                                 "localPermitDocument",
                                 event?.target?.files?.[0]
                              );
                           }}
                           value={getFieldProps("localPermitDocument").value}
                           errors={formik.errors.localPermitDocument}
                           message={formik.errors.localPermitDocument}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           // require
                           label="National Permit Expiry Date"
                           placeholder="Enter National Permit Expiry Date"
                           {...getFieldProps("nationalPermitExpiryDate")}
                           errors={formik.errors.nationalPermitExpiryDate}
                           message={formik.errors.nationalPermitExpiryDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           // require
                           label="National Permit Amount"
                           id="fitnessAmount"
                           placeholder="Enter National Permit Amount"
                           {...getFieldProps("nationalPermitAmount")}
                           errors={formik.errors.nationalPermitAmount}
                           message={formik.errors.nationalPermitAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           National Permit Document
                           {/* <span className="text-danger">*</span> */}
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue(
                                 "nationalPermitDocument",
                                 event?.target?.files?.[0]
                              );
                           }}
                           value={getFieldProps("nationalPermitDocument").value}
                           errors={formik.errors.nationalPermitDocument}
                           message={formik.errors.nationalPermitDocument}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="PUC Expiry Date"
                           placeholder="Enter PUC Expiry Date"
                           {...getFieldProps("pucExpiryDate")}
                           errors={formik.errors.pucExpiryDate}
                           message={formik.errors.pucExpiryDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="PUC Amount"
                           id="fitnessAmount"
                           placeholder="Enter PUC Amount"
                           {...getFieldProps("pucAmount")}
                           errors={formik.errors.pucAmount}
                           message={formik.errors.pucAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           PUC Document
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue("pucDocument", event?.target?.files?.[0]);
                           }}
                           value={getFieldProps("pucDocument").value}
                           errors={formik.errors.pucDocument}
                           message={formik.errors.pucDocument}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           createdBy
                           label="MV Tax Expiry Date"
                           placeholder="Enter MV Tax Expiry Date"
                           {...getFieldProps("mvTaxExpiryDate")}
                           errors={formik.errors.mvTaxExpiryDate}
                           message={formik.errors.mvTaxExpiryDate}
                        />
                     </div>

                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="MV Tax Amount"
                           id="fitnessAmount"
                           placeholder="Enter MV Tax Amount"
                           {...getFieldProps("mvTaxAmount")}
                           errors={formik.errors.mvTaxAmount}
                           message={formik.errors.mvTaxAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           MV Tax
                           <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue("mvTax", event?.target?.files?.[0]);
                           }}
                           value={getFieldProps("mvTax").value}
                           errors={formik.errors.mvTax}
                           message={formik.errors.mvTax}
                        />
                     </div>

                     <div className="col-lg-6">
                        <CustomInput
                           label="GPS Provider Name"
                           id="gpsProvider"
                           placeholder="Enter GPS Provider Name"
                           {...getFieldProps("gpsProviderName")}
                           errors={formik.errors.gpsProviderName}
                           message={formik.errors.gpsProviderName}
                        />
                     </div>
                     <div className="col-lg-6">
                        <CustomInput
                           label="GPS Amount"
                           id="fitnessAmount"
                           placeholder="Enter GPS Amount"
                           {...getFieldProps("gpsAmount")}
                           errors={formik.errors.gpsAmount}
                           message={formik.errors.gpsAmount}
                        />
                     </div>
                     <div className="col-lg-6">
                        <CustomInput
                           label="Fabricator Name"
                           id="fitnessAmount"
                           placeholder="Enter Fabricator Name"
                           {...getFieldProps("fabricatorName")}
                           errors={formik.errors.fabricatorName}
                           message={formik.errors.fabricatorName}
                        />
                     </div>

                     <div className="col-lg-6">
                        <CustomInput
                           label="Fabricator Location"
                           id="fitnessAmount"
                           placeholder="Enter Fabricator Location"
                           {...getFieldProps("fabricatorLocation")}
                           errors={formik.errors.fabricatorLocation}
                           message={formik.errors.fabricatorLocation}
                        />
                     </div>
                  </div>
               </div>

               <div className="card card-primary mt-5">
                  {/* --------------------------- EMI --------------------------------- */}
                  <div className="card-header">
                     <h3 className="card-title">EMI Details</h3>
                  </div>

                  <div className="row card-body">
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="EMI Start date"
                           placeholder="Enter vehicle owner name"
                           {...getFieldProps("emiStartDate")}
                           errors={
                              Boolean(touched.emiStartDate) &&
                              Boolean(errors.emiStartDate)
                           }
                           message={errors.emiStartDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomDatePicker
                           require
                           label="EMI End date"
                           placeholder="Enter vehicle owner name"
                           {...getFieldProps("emiEndDate")}
                           errors={
                              Boolean(touched.emiEndDate) && Boolean(errors.emiEndDate)
                           }
                           message={errors.emiEndDate}
                        />
                     </div>
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="EMI Amount"
                           id="emiAmount"
                           placeholder="Enter Amount"
                           {...getFieldProps("emiAmount")}
                           errors={formik.errors.emiAmount}
                           message={formik.errors.emiAmount}
                        />
                     </div>
                     <div className="col-lg-4">
                        <label className="text-bold">
                           EMI Certificate <span className="text-danger">*</span>
                        </label>
                        <CustomFileUpload
                           accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                           onChange={(event) => {
                              setFieldValue("emiCertificate", event?.target?.files?.[0]);
                           }}
                           value={getFieldProps("emiCertificate").value}
                           errors={
                              Boolean(touched.emiCertificate) &&
                              Boolean(errors.emiCertificate)
                           }
                           message={errors.emiCertificate}
                        />
                     </div>
                     {/* Financed by sec */}
                     <div className="col-lg-4">
                        <CustomInput
                           require
                           label="Financed by"
                           id="#financedBy"
                           placeholder="Enter name"
                           {...getFieldProps("financedBy")}
                           errors={
                              Boolean(touched.financedBy) && Boolean(errors.financedBy)
                           }
                           message={errors.financedBy}
                        />
                     </div>
                  </div>
               </div>

               <div className="card-body mt-5">
                  <button
                     className="btn btn-primary px-4 py-3 float-right ml-3"
                     type="button"
                     onClick={handleSubmit}
                  >
                     <h6 className="mb-0 text-uppercase">Submit</h6>
                  </button>
               </div>
            </div>
         </Form>
      </FormikProvider>
   );
}

export default VehicleDetails;
